import { useEffect, useState } from 'react';
import { line, BOM_LINE_SOURCES, BOM_LINE_SOURCE_LABELS, Color, DEFAULT_CURRENCY_CODE, fill, Size, dollarsToCents, formatCurrency, sequenceRows, t, type BomCostChangeAsk, type BomCostChangeMade, type BomCostChangeReply, type BomLineSource, type BomLineStock, type BomLockReason, type BomRowKind, type ValueRaise } from '@bms2/shared';
import { Button, Checkbox, Select } from '../../components/controls';
import { ItemTable, isDivider, isUntouchedLine, newLine, type ItemLineDraft } from '../../components/composites/ItemTable';
import { downloadThroughFrame } from '../reports/api';
import { apiFetch, post, DEALS_PATH } from './api';
import { BomCostChangeModal, costChangeAskOf } from './BomCostChange';
import { useItemPicker } from '../settings/api';
import { RfqModal } from './RfqModal';
import pageStyles from './RecordPage.module.css';
import styles from './DealBomEditor.module.css';

/** A BOM line as the Deal page hands it to the editor: the stored row, cost only where the viewer sees it (decision 0276). */
export interface BomEditorLine {
  id: string;
  itemId: string;
  /** The Item's name as the Deal page's answer names it. */
  itemLabel?: string;
  /** 🔨 The same name as the list's own answer names it (found while building, batch J6): read where `itemLabel` is absent. */
  itemName?: string;
  sku: string;
  quantity: number;
  unitName: string;
  /** The line's place in the BOM's one ordering, shared with the dividers (decision 0333). */
  position: number;
  description: string | null;
  purchasePrice: number | null;
  isProjected: boolean;
  stale: boolean;
  /** 🔒 The hours a service line budgets and whether the line is a Service (decision 0860); absent from an older reply. */
  budgetedHours?: number | null;
  isService?: boolean;
  /** 🔒 Buy or From Stock on a goods line (decision 3320); absent from an older reply, which reads as Buy. */
  source?: BomLineSource;
  /** 🔒 The shelf beside a goods line (decision 3320); null on a Service line, absent from an older reply. */
  stock?: BomLineStock | null;
}

/** A named group divider in the list (decision 0333): the lines beneath it, down to the next divider, are its group. */
export interface BomEditorDivider {
  id: string;
  name: string;
  position: number;
}

export interface DealBomEditorProps {
  /** The list's own id (decision 0565): every route the editor writes takes it, never the Deal's. */
  bomId: string;
  /**
   * The Deal the list was reached from, else its newest attached Deal (decision 1090); the request for
   * quotation is that Deal's and its picker offers every list's lines (decision 0565). Null on a list
   * attached to no Deal, which offers no request for quotation.
   */
  dealId: string | null;
  lines: readonly BomEditorLine[];
  dividers?: readonly BomEditorDivider[];
  /** The accounting audience sees the rate and amount columns; anyone else edits the list without its money. */
  seesCost: boolean;
  /** False on a list whose every Deal is done, which is kept for reference and never edited (piece 18; decisions 1090, 1466, 1512). */
  editable: boolean;
  /** Why a list that is not editable is locked, which the line above its read-only grid says (decision 1466); a Lost Deal's reason where absent. */
  lockReason?: BomLockReason | null;
  /** 🔒 The names of the Won Deals that lock the list, which the reason names (decision 1512); the list's own page passes them, the Deal's tile does not. */
  wonDeals?: readonly string[];
  /**
   * 🔒 The Deal's Bill of materials tile draws each list here, read-only
   * (decision 0573): the grid alone, in the columns this page draws, with no
   * footer and nothing to type into. A line is changed on the list's own page.
   */
  readOnly?: boolean;
  apiBaseUrl?: string;
  onSaved: (message: string) => void;
  onError: (message: string) => void;
  /** Told whenever the grid gains or loses unsaved rows, so the page can hold Done until they are saved (decision 1054). */
  onDirtyChange?: (dirty: boolean) => void;
}

// The group is the divider above the line, so the table carries no group column (decision 0333); the box under the name is the description (decision 0303).
// 🔒 Budget (hours) stands beside the quantity, drawn on a service line alone (decision 0860).
const COST_COLUMNS = ['item', 'description', 'qty', 'budget', 'unit', 'rate', 'amount'] as const;
const PLAIN_COLUMNS = ['item', 'description', 'qty', 'budget', 'unit'] as const;

/**
 * The list's download routes, per bill of materials since decision 0565, relative to the API root like every path here.
 * 🔒 The PDF is the list's printed sheet through the one renderer since decision 0862, the same bytes the page's PDF
 * view downloads; the Excel sheet keeps its own writer.
 */
export const BOM_PDF_PATH = (bomId: string) => `${DEALS_PATH}/boms/${bomId}/pdf`;
export const BOM_XLSX_PATH = (bomId: string) => `${DEALS_PATH}/boms/${bomId}/export.xlsx`;
/** 🔒 The list's one Save (decision 1466): every row in order and the rows removed, in one request and one transaction. */
export const BOM_SAVE_PATH = (bomId: string) => `${DEALS_PATH}/boms/${bomId}/save`;

/** One row as the Save sends it: a stored row by its id and only what moved, a fresh one in full. */
type SaveRow = { kind: BomRowKind; id?: string; name?: string; itemId?: string | null; quantity?: number; unitId?: string; description?: string | null; purchasePrice?: number; budgetedHours?: number | null; source?: BomLineSource; stockSiteId?: string | null };
interface SaveBody {
  rows: SaveRow[];
  removed: { kind: BomRowKind; id: string }[];
  urgent: boolean;
}

function toDraft(row: BomEditorLine): ItemLineDraft {
  return {
    id: row.id,
    itemId: row.itemId,
    itemLabel: row.itemLabel ?? row.itemName ?? '',
    sku: row.sku,
    description: row.description ?? '',
    qty: row.quantity,
    unit: row.unitName,
    rateCents: row.purchasePrice === null ? null : dollarsToCents(row.purchasePrice),
    freeText: false,
    service: row.isService === true,
    budgetedHours: row.budgetedHours ?? null,
    source: row.source ?? 'Buy',
    stockSiteId: row.stock?.siteId ?? null,
  };
}

function dividerDraft(row: BomEditorDivider): ItemLineDraft {
  return { id: row.id, kind: 'divider', itemId: null, itemLabel: row.name, description: '', qty: null, unit: '', rateCents: null, freeText: false };
}

/** The stored lines and dividers as one list in the BOM's own order (decision 0333). */
export function toDrafts(lines: readonly BomEditorLine[], dividers: readonly BomEditorDivider[]): ItemLineDraft[] {
  const byId = new Map<string, ItemLineDraft>([...lines.map((l) => [l.id, toDraft(l)] as const), ...dividers.map((d) => [d.id, dividerDraft(d)] as const)]);
  return sequenceRows([...lines.map((l) => ({ kind: 'line' as const, id: l.id, position: l.position })), ...dividers.map((d) => ({ kind: 'divider' as const, id: d.id, position: d.position, name: d.name }))]).map((row) => byId.get(row.id)!);
}

const isNew = (draft: ItemLineDraft) => draft.id.startsWith('line-');
// 🔨 The unit is compared too, or a line whose only change is its unit reads unchanged and the save writes nothing (found in review, 2026-09-16).
const same = (a: ItemLineDraft, b: ItemLineDraft) => a.qty === b.qty && a.description === b.description && a.itemLabel === b.itemLabel && a.rateCents === b.rateCents && (a.unitId ?? null) === (b.unitId ?? null) && (a.budgetedHours ?? null) === (b.budgetedHours ?? null) && (a.source ?? 'Buy') === (b.source ?? 'Buy') && (a.stockSiteId ?? null) === (b.stockSiteId ?? null);
const kindOf = (draft: ItemLineDraft): BomRowKind => (isDivider(draft) ? 'divider' : 'line');

/**
 * The Bill of materials tile's own construction (piece 19; decision 0291): the
 * Item Table on the BOM host, the one list procurement and the Quote both read
 * (decision 0181), edited in place and written by one Save. A row's Item is the
 * catalog Dropdown, the box under it is the line's description, and the rate
 * column reads and writes the line's `purchasePrice` snapshot where the viewer
 * sees cost. A named divider is a row of its own, and the rows drag up and
 * down across the dividers (decision 0333); Save writes the lines, the
 * dividers and the one order in one request (decision 1466). 🔨 A free-text row has no place here:
 * every BOM line names an Item. Export offers the list as a PDF or a sheet.
 */
/**
 * 🔒 What the editor opens on (decision 0861, the owner's T141): the stored rows,
 * or one blank line where the list holds none, the way the Work Order's editor
 * opens. The row is the instruction, so no empty-state sentence stands above it.
 */
function openingDrafts(lines: readonly BomEditorLine[], dividers: readonly BomEditorDivider[]): ItemLineDraft[] {
  const stored = toDrafts(lines, dividers);
  return stored.length > 0 ? stored : [newLine(false)];
}

/** 🔨 An untouched blank line is not a change and not a line (decision 0861): the save drops it rather than refusing it. */
const typedRows = (drafts: readonly ItemLineDraft[]): ItemLineDraft[] => drafts.filter((draft) => !isUntouchedLine(draft));

export function DealBomEditor({ bomId, dealId, lines, dividers = [], seesCost, editable, lockReason = null, wonDeals = [], readOnly = false, apiBaseUrl = '', onSaved, onError, onDirtyChange }: DealBomEditorProps) {
  const [drafts, setDrafts] = useState<ItemLineDraft[]>(() => openingDrafts(lines, dividers));
  const [urgent, setUrgent] = useState(false);
  const [rfqOpen, setRfqOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  /** 🔒 The documents a costed save owes, while the confirmation is open, and the body it will send again with the answer (decision 1466). */
  const [asked, setAsked] = useState<{ ask: BomCostChangeAsk; body: SaveBody } | null>(null);
  const stored = JSON.stringify([lines, dividers]);
  // A reload after Save, or another tile's write, replaces the drafts; the state adjusts during the render that sees the new lines.
  const [seen, setSeen] = useState(stored);
  if (seen !== stored) {
    setSeen(stored);
    setDrafts(openingDrafts(lines, dividers));
  }
  const initial = toDrafts(lines, dividers);
  const dirty = JSON.stringify(typedRows(drafts)) !== JSON.stringify(initial);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // The BOM is the purchase side: the Item's purchase price and its purchase description seed a picked line (decision 0303).
  const { itemSearch, units, skus, prices, descriptions, services } = useItemPicker(apiBaseUrl, 'purchase');

  /** The Save's body: every row in the order the grid shows them, the rows removed, and the urgent flag new lines take (decision 1466). */
  const bodyOf = (rows: readonly ItemLineDraft[]): SaveBody => {
    const kept = new Set(rows.map((draft) => draft.id));
    const removed = initial.filter((row) => !kept.has(row.id)).map((row) => ({ kind: kindOf(row), id: row.id }));
    const sent = rows.map((draft): SaveRow => {
      const before = initial.find((row) => row.id === draft.id);
      if (isDivider(draft)) {
        if (isNew(draft)) return { kind: 'divider', name: draft.itemLabel.trim() };
        return before && before.itemLabel !== draft.itemLabel ? { kind: 'divider', id: draft.id, name: draft.itemLabel.trim() } : { kind: 'divider', id: draft.id };
      }
      const price = seesCost && draft.rateCents !== null ? { purchasePrice: draft.rateCents / 100 } : {};
      if (isNew(draft)) {
        return { kind: 'line', itemId: draft.itemId, quantity: draft.qty ?? 1, description: draft.description.trim() || null, ...(draft.unitId ? { unitId: draft.unitId } : {}), ...price, ...(draft.service ? { budgetedHours: draft.budgetedHours ?? null } : {}), ...(draft.source === 'FromStock' && !draft.service ? { source: draft.source } : {}) };
      }
      if (!before || same(before, draft)) return { kind: 'line', id: draft.id };
      const stale = lines.find((row) => row.id === draft.id);
      return {
        kind: 'line',
        id: draft.id,
        quantity: draft.qty ?? before.qty ?? 1,
        description: draft.description.trim() || null,
        // 🔨 The unit the grid's closed list wrote is what the line saves, or the override is discarded silently (decision 0439).
        ...(draft.unitId ? { unitId: draft.unitId } : {}),
        // A projected price is the system's and is not typed over (piece 18); only a changed, typed rate is sent.
        ...(stale?.isProjected || draft.rateCents === before.rateCents ? {} : price),
        // 🔒 A service line's budget, sent where it moved (decision 0860).
        ...(draft.service && (draft.budgetedHours ?? null) !== (before.budgetedHours ?? null) ? { budgetedHours: draft.budgetedHours ?? null } : {}),
        // 🔒 Buy or From Stock, sent where it moved (decision 3320).
        ...((draft.source ?? 'Buy') !== (before.source ?? 'Buy') ? { source: draft.source ?? 'Buy' } : {}),
        // 🔒 The shelf a From Stock line is reserved at, sent where it moved (decision 3321).
        ...((draft.stockSiteId ?? null) !== (before.stockSiteId ?? null) ? { stockSiteId: draft.stockSiteId ?? null } : {}),
      };
    });
    return { rows: sent, removed, urgent };
  };

  /**
   * 🔒 One request carries the whole Save (decision 1466). Where it moves the
   * list's cost after a Quote drawn from it went out, the server answers with
   * the documents it owes, the confirmation opens, and the same body goes again
   * with Yes or a holder's typed reason.
   */
  const send = async (body: SaveBody, reply: BomCostChangeReply | null) => {
    setSaving(true);
    try {
      const answer = await post<{ valueRaised?: ValueRaise | null; costChange?: BomCostChangeMade | null }>(BOM_SAVE_PATH(bomId), reply ? { ...body, costChange: reply } : body, apiBaseUrl);
      if (!answer.ok) {
        const ask = answer.status === 409 ? costChangeAskOf(answer.body) : null;
        if (ask) return setAsked({ ask, body });
        setAsked(null);
        return onError(answer.message);
      }
      setAsked(null);
      const lifted = answer.data.valueRaised ?? null;
      const made = answer.data.costChange ?? null;
      const documents = made ? made.made.map((doc) => line(t('deal.bom.costChange.opened'), doc.number)) : [];
      onSaved(line(
        t('deal.bom.saved'),
        ...(lifted ? [t('deal.bom.valueRaised'), formatCurrency({ amount: lifted.toCents, currencyCode: DEFAULT_CURRENCY_CODE }), t('deal.bom.valueRaisedReason')] : []),
        ...documents,
        made?.overridden ? t('deal.bom.costChange.overridden') : null,
      ));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const rows = typedRows(drafts);
    if (rows.some((draft) => !isDivider(draft) && (draft.freeText || !draft.itemId))) return onError(t('deal.bom.needsItem'));
    if (rows.some((draft) => isDivider(draft) && !draft.itemLabel.trim())) return onError(t('deal.bom.needsDividerName'));
    await send(bodyOf(rows), null);
  };

  /** The download rides the frame's own headers, so it goes through the one download path and is saved from a blob rather than a bare link (T541). */
  const exportAs = async (kind: 'pdf' | 'xlsx') => {
    setExporting(true);
    try {
      const saved = await downloadThroughFrame(apiFetch, `${apiBaseUrl}${kind === 'pdf' ? BOM_PDF_PATH(bomId) : BOM_XLSX_PATH(bomId)}`, `bom.${kind}`);
      if (!saved) onError(t('deal.bom.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const exportButton = (
    <span data-bom-action="export">
      <Button size={Size.Small} color={Color.Inactive} text={[t('deal.bom.exportPdf'), t('deal.bom.exportXlsx')]} actions={[() => void exportAs('pdf'), () => void exportAs('xlsx')]} disabled={lines.length === 0 || exporting} />
    </span>
  );

  /** 🔒 Buy or From Stock under a goods line, with the shelf beside it (decision 3320); a Service line and an empty row draw none. */
  const sourceMark = (draft: ItemLineDraft, read: boolean, patchLine: (change: Partial<ItemLineDraft>) => void) => (
    <BomSourceMark draft={draft} stock={lines.find((row) => row.id === draft.id)?.stock ?? null} read={read} service={draft.service === true || (draft.itemId !== null && services.current.has(draft.itemId))} onPick={(source) => patchLine({ source })} onSite={(stockSiteId) => patchLine({ stockSiteId })} />
  );

  // 🔨 The tile's own drawing: the grid and nothing else, so a list on a live Deal reads there without offering an Export or naming a Lost Deal.
  if (readOnly) {
    return (
      <div className={styles.editor} data-bom-editor="readOnly">
        <ItemTable documentType="BOM" columns={seesCost ? COST_COLUMNS : PLAIN_COLUMNS} lines={initial} onChange={() => undefined} discountLevel="Footer" itemSearch={itemSearch} lineMark={sourceMark} readOnly />
      </div>
    );
  }
  if (!editable) {
    return (
      <div className={styles.editor} data-bom-editor="locked" data-bom-lock={lockReason ?? 'lost'}>
        {/* 🔒 The reason stands above the read-only grid (decision 1466): every Deal Lost, or every Deal done with a Won among them, named where the page knows them (decision 1512). */}
        <p className={pageStyles.hint} data-bom-lock-reason={lockReason ?? 'lost'}>{lockReason !== 'done' ? t('deal.bom.lost') : wonDeals.length > 0 ? fill(t('deal.bom.won'), { deals: wonDeals.join(', ') }) : t('deal.bom.done')}</p>
        {/* 🔨 A locked list is the read-only grid, never an editing grid whose typing goes nowhere (T200; decision 0649). */}
        <ItemTable documentType="BOM" columns={seesCost ? COST_COLUMNS : PLAIN_COLUMNS} lines={initial} onChange={() => undefined} discountLevel="Footer" itemSearch={itemSearch} lineMark={sourceMark} readOnly />
        <div className={styles.footer}>{exportButton}</div>
      </div>
    );
  }
  return (
    <div className={styles.editor} data-bom-editor={dirty ? 'dirty' : 'clean'}>
      {/* 🔨 No empty-state sentence: an empty list opens on one blank line, and the row is the instruction (decision 0861). */}
      <ItemTable
        documentType="BOM"
        columns={seesCost ? COST_COLUMNS : PLAIN_COLUMNS}
        {...(seesCost ? { seedRateCents: (itemId: string) => prices.current.get(itemId) ?? null } : {})}
        descriptionOf={(itemId) => descriptions.current.get(itemId) ?? null}
        serviceOf={(itemId) => services.current.has(itemId)}
        lines={drafts}
        onChange={setDrafts}
        discountLevel="Footer"
        itemSearch={itemSearch}
        skuOf={(itemId) => skus.current.get(itemId) ?? null}
        unitOf={(itemId) => units.current.get(itemId) || null}
        lineMark={sourceMark}
      />
      <div className={styles.footer}>
        <Checkbox label={t('deal.bom.urgent')} checked={urgent} size={Size.Small} onChange={setUrgent} />
        {/* Request for quotation: the ticked lines to one vendor, PDF and sheet attached (piece 23; decision 0316). */}
        {dealId === null ? null : (
          <span data-bom-action="rfq">
            <Button size={Size.Small} text={[t('deal.rfq.action')]} actions={[() => setRfqOpen(true)]} disabled={lines.length === 0 || dirty || saving} />
          </span>
        )}
        {exportButton}
        <span className={styles.spacer} />
        <span data-bom-action="discard">
          <Button size={Size.Small} color={Color.Inactive} text={[t('deal.bom.discard')]} actions={[() => setDrafts(openingDrafts(lines, dividers))]} disabled={!dirty || saving} />
        </span>
        <span data-bom-action="save">
          <Button size={Size.Small} color={Color.Navy} text={[t('deal.bom.save')]} actions={[() => void save()]} disabled={!dirty || saving} />
        </span>
      </div>
      {asked ? (
        <BomCostChangeModal
          ask={asked.ask}
          saving={saving}
          onConfirm={() => void send(asked.body, { confirm: true })}
          onOverride={(reason) => void send(asked.body, { overrideReason: reason })}
          onCancel={() => setAsked(null)}
        />
      ) : null}
      {dealId === null ? null : <RfqModal
        key={rfqOpen ? 'rfq-open' : 'rfq-closed'}
        open={rfqOpen}
        dealId={dealId}
        apiBaseUrl={apiBaseUrl}
        onCancel={() => setRfqOpen(false)}
        onSent={(answer) => {
          setRfqOpen(false);
          onSaved(answer.send === 'Accepted' || answer.send === 'Deferred' ? line(answer.rfqNumber, t('deal.rfq.sent'), answer.to) : line(answer.rfqNumber, t('deal.rfq.sendFailed')));
        }}
      />}
    </div>
  );
}

/**
 * 🔒 A goods line's source under its description (decision 3320): a picker
 * while the list is edited and a word once it reads, with what our sites hold
 * of the Item beside it. A Service line and a row naming no Item draw nothing.
 */
function BomSourceMark({ draft, stock, read, service, onPick, onSite }: { draft: ItemLineDraft; stock: BomLineStock | null; read: boolean; service: boolean; onPick: (source: BomLineSource) => void; onSite: (siteId: string | null) => void }) {
  if (service || draft.itemId === null || draft.kind === 'divider') return null;
  const source = draft.source ?? 'Buy';
  const shelf = stock ? <span className={styles.shelf} data-bom-on-hand={String(stock.onHand)}>{t('deal.bom.onHand')} {stock.onHand} {stock.unitName}</span> : null;
  // 🔒 What the stored From Stock line holds, has had picked and cannot cover (decisions 3321, 3322); a change not yet saved reads none.
  const stored = stock !== null && source === 'FromStock' && (stock.siteId !== null || stock.reserved > 0 || stock.short > 0);
  const held = stored ? (
    <>
      <span className={styles.shelf} data-bom-reserved={String(stock.reserved)}>{t('deal.bom.reserved')} {stock.reserved}</span>
      {stock.picked > 0 ? <span className={styles.shelf} data-bom-picked={String(stock.picked)}>{t('deal.bom.picked')} {stock.picked}</span> : null}
      {stock.short > 0 ? <span className={styles.short} data-bom-short={String(stock.short)}>{t('deal.bom.short')} {stock.short}</span> : null}
    </>
  ) : null;
  if (read) {
    return (
      <span className={styles.sourcing} data-bom-source={source}>
        <span>{BOM_LINE_SOURCE_LABELS[source]}{source === 'FromStock' && stock?.siteName ? ` ${t('deal.bom.at')} ${stock.siteName}` : ''}</span>
        {shelf}
        {held}
      </span>
    );
  }
  // 🔒 The shelf is chosen on the line among our sites holding the Item, the one holding the most until changed (decision 3321).
  const sites = stock?.sites ?? [];
  const sitePick = source === 'FromStock' && stock !== null ? (
    <Select
      size={Size.Small}
      label={t('deal.bom.stockSite')}
      labelHidden
      value={draft.stockSiteId ?? ''}
      onChange={(next) => onSite(next === '' ? null : next)}
      options={sites.map((site) => ({ value: site.siteId, label: `${site.siteName} · ${site.onHand} ${stock.unitName}` }))}
      placeholder={sites.length === 0 ? t('deal.bom.stockSite.none') : t('deal.bom.stockSite')}
      disabled={sites.length === 0}
      dataAttribute={{ 'data-bom-site-pick': draft.id }}
    />
  ) : null;
  return (
    <div className={styles.sourcing} data-bom-source={source} title={t('deal.bom.source.hint')}>
      <Select
        size={Size.Medium}
        label={t('deal.bom.source')}
        labelHidden
        value={source}
        onChange={(next) => onPick(next as BomLineSource)}
        options={BOM_LINE_SOURCES.map((value) => ({ value, label: BOM_LINE_SOURCE_LABELS[value] }))}
        dataAttribute={{ 'data-bom-source-pick': draft.id }}
      />
      {sitePick}
      {shelf}
      {held}
    </div>
  );
}
